import { classifyPlacement, type PlacementDerivedMetrics } from '@/utils/workspacePlacementMetrics';
import type { MetricPolarity, WorkspaceMetricColumnDef } from '@/utils/workspaceAdProductMetrics';
import { percentChangeVsBaseline } from '@/api/snapshotMetrics';

/** Bid-adjustable placements shown in campaign-table columns (homepage omitted). */
export type CampaignPlacementBidKey =
  | 'top_of_search'
  | 'rest_of_search'
  | 'product_pages';

/** Fixed row order for campaign-table placement columns. */
export const CAMPAIGN_PLACEMENT_LINES: Array<{ id: CampaignPlacementBidKey; label: string }> = [
  { id: 'top_of_search', label: 'Top of Search' },
  { id: 'rest_of_search', label: 'Rest of Search' },
  { id: 'product_pages', label: 'Product Pages' },
];

export type CampaignPlacementBidPercents = Record<CampaignPlacementBidKey, number | null>;

function emptyBidPercents(): CampaignPlacementBidPercents {
  return {
    top_of_search: null,
    rest_of_search: null,
    product_pages: null,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function readPercentage(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function applyBidEntries(
  out: CampaignPlacementBidPercents,
  entries: unknown
): void {
  if (!Array.isArray(entries)) return;
  for (const item of entries) {
    const rec = asRecord(item);
    if (!rec) continue;
    const bucket = classifyPlacement(rec.placement);
    if (bucket === 'homepage' || bucket == null) continue;
    if (!(bucket in out)) continue;
    const pct = readPercentage(rec.percentage);
    if (pct == null) continue;
    out[bucket as keyof CampaignPlacementBidPercents] = pct;
  }
}

/**
 * Parse SP `dynamicBidding.placementBidding` or SB `bidding.bidAdjustmentsByPlacement`
 * from a campaign list payload (`raw` or the list object itself).
 */
export function parseCampaignPlacementBidPercents(
  raw: Record<string, unknown> | null | undefined
): CampaignPlacementBidPercents {
  const out = emptyBidPercents();
  if (!raw) return out;

  const dynamicBidding = asRecord(raw.dynamicBidding) ?? asRecord(raw.dynamic_bidding);
  if (dynamicBidding) {
    applyBidEntries(out, dynamicBidding.placementBidding);
  }

  const bidding = asRecord(raw.bidding);
  if (bidding) {
    applyBidEntries(out, bidding.bidAdjustmentsByPlacement);
  }

  return out;
}

export function formatPlacementBidPercent(value: number | null): string {
  if (value == null) return '—';
  return `${value}%`;
}

export type PlacementCardMetricDef = {
  key: keyof PlacementDerivedMetrics;
  label: string;
  polarity: MetricPolarity;
  format: WorkspaceMetricColumnDef['format'];
};

/** Collapsed card rows: Sales, Spend, ACoS. */
export const PLACEMENT_CARD_PRIMARY_METRICS: PlacementCardMetricDef[] = [
  { key: 'sales30d', label: 'Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'cost', label: 'Spend', polarity: 'lower_is_better', format: 'currency' },
  { key: 'acos', label: 'ACoS', polarity: 'lower_is_better', format: 'percent' },
];

/** Hover "More" left column. Units omitted — not in the placement API. */
export const PLACEMENT_MORE_LEFT_METRICS: PlacementCardMetricDef[] = [
  { key: 'sales30d', label: 'Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'purchases30d', label: 'Orders', polarity: 'higher_is_better', format: 'int' },
  { key: 'impressions', label: 'Impr.', polarity: 'higher_is_better', format: 'int' },
  { key: 'ctr', label: 'CTR', polarity: 'higher_is_better', format: 'percent' },
  { key: 'cr', label: 'CR%', polarity: 'higher_is_better', format: 'percent' },
  { key: 'roas', label: 'ROAS', polarity: 'higher_is_better', format: 'ratio' },
];

/** Hover "More" right column. */
export const PLACEMENT_MORE_RIGHT_METRICS: PlacementCardMetricDef[] = [
  { key: 'cost', label: 'Spend', polarity: 'lower_is_better', format: 'currency' },
  { key: 'clicks', label: 'Clicks', polarity: 'higher_is_better', format: 'int' },
  { key: 'cpc', label: 'CPC', polarity: 'lower_is_better', format: 'currency' },
  { key: 'acos', label: 'ACoS', polarity: 'lower_is_better', format: 'percent' },
];

export function placementHasActivity(
  metrics: PlacementDerivedMetrics | null | undefined
): boolean {
  if (!metrics) return false;
  return (
    metrics.impressions > 0 ||
    metrics.clicks > 0 ||
    metrics.cost > 0 ||
    metrics.sales30d > 0
  );
}

export type PlacementDeltaTone = 'good' | 'bad' | 'neutral';

/** Color of a compare delta using workspace polarity (not raw up=green). */
export function placementDeltaTone(
  current: number,
  previous: number,
  polarity: MetricPolarity
): PlacementDeltaTone {
  const pct = percentChangeVsBaseline(current, previous);
  if (pct.type !== 'ok') return 'neutral';
  if (pct.value === 0) return 'neutral';
  const isUp = pct.value > 0;
  const isGood = polarity === 'higher_is_better' ? isUp : !isUp;
  return isGood ? 'good' : 'bad';
}
